import { describeApiError, type PointsSystem } from '@brush/api-client';
import { ErrorNotice, PointsSystemList, currentVersion } from '@brush/ui';
import { useMemo, useState } from 'react';
import {
  useAllLeaderboards,
  useDeletePointsSystem,
  usePlatformPointsSystems,
  useSetPointsSystemArchived,
} from '../../queries';
import { AreaGate } from '../areas/AreaGate';
import { Dialog } from './Dialog';
import { NewPointsSystemDialog, type NewSystemIntent } from './NewPointsSystemDialog';
import { PointsSystemBuilderScreen } from './PointsSystemBuilderScreen';

/**
 * The platform's points system catalogue: the list, and the builder it opens into.
 *
 * <b>Two screens, deliberately kept apart.</b> A system is authored here without reference to any
 * leaderboard, and a leaderboard consumes a published one from its own screen. Nothing about a
 * leaderboard is configurable from the builder and no system can be authored from a leaderboard —
 * that separation is what makes systems reusable, and conflating them is the design risk the
 * specification calls out by name.
 *
 * The list and the builder are one area rather than two routes because neither console routes by
 * URL: every section here swaps what is on screen, the way Templates and Rooms already do.
 */
export function PointsSystemsArea() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [intent, setIntent] = useState<NewSystemIntent | null>(null);
  const [deleting, setDeleting] = useState<PointsSystem | null>(null);

  const systems = usePlatformPointsSystems(includeArchived);
  const leaderboards = useAllLeaderboards();
  const archive = useSetPointsSystemArchived();

  const rows = useMemo(() => systems.data ?? [], [systems.data]);

  /**
   * How many leaderboards have pinned each system. Counted from the leaderboards the server
   * returned rather than asked for — there is no usage count on the DTO, and inventing one is
   * exactly what the "never fabricate numbers" rule exists to stop.
   */
  const usage = useMemo(() => {
    if (!leaderboards.data) return null;
    const counts = new Map<string, number>();
    for (const leaderboard of leaderboards.data) {
      if (leaderboard.pointsSystemId) {
        counts.set(leaderboard.pointsSystemId, (counts.get(leaderboard.pointsSystemId) ?? 0) + 1);
      }
    }
    // Every system gets an entry, so a system nothing uses reads as 0 rather than as unknown.
    return new Map(rows.map((s) => [s.id, counts.get(s.id) ?? 0]));
  }, [leaderboards.data, rows]);

  const open = openId ? rows.find((s) => s.id === openId) ?? null : null;

  // The system is looked up by id every render rather than held, so the builder sees a new
  // version appear the moment the catalogue refetches.
  if (open) {
    return <PointsSystemBuilderScreen system={open} onBack={() => setOpenId(null)} />;
  }

  return (
    <AreaGate
      queries={[systems]}
      loadingLabel="Loading points systems…"
      errorMessage="Couldn't load the points systems."
    >
      <PointsSystemList
        systems={rows}
        usage={usage}
        usageLabel="Leaderboards"
        usageHint="Leaderboards across every room that have pinned a version of this system."
        includeArchived={includeArchived}
        onIncludeArchivedChange={setIncludeArchived}
        onNew={(start) => setIntent({ mode: 'create', start })}
        onOpen={(row) => setOpenId(row.id)}
        onClone={(row) => {
          const system = rows.find((s) => s.id === row.id);
          const version = system ? currentVersion(system) : null;
          if (system && version) {
            setIntent({ mode: 'duplicate', source: system, versionNumber: version.versionNumber });
          }
        }}
        onArchive={(row) => archive.mutate({ id: row.id, archived: true })}
        onUnarchive={(row) => archive.mutate({ id: row.id, archived: false })}
        onDelete={(row) => setDeleting(rows.find((s) => s.id === row.id) ?? null)}
        // Every system on this screen is the platform's, so an administrator manages all of them.
        canManage={() => true}
        nameOf={(id) => rows.find((s) => s.id === id)?.name ?? null}
        notice={
          leaderboards.isError ? (
            <p className="text-xs text-ink-800/55">
              Couldn’t read the leaderboards, so the usage column is blank rather than wrong.
            </p>
          ) : null
        }
      />

      <ErrorNotice message={describeApiError(archive.error)} className="mt-4" />

      {intent ? (
        <NewPointsSystemDialog
          key={intent.mode === 'create' ? `new-${intent.start}` : `copy-${intent.source.id}`}
          intent={intent}
          onClose={() => setIntent(null)}
          onCreated={(created) => {
            setIntent(null);
            setOpenId(created.id);
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteDialog
          system={deleting}
          onClose={() => setDeleting(null)}
          onArchived={() => {
            archive.mutate({ id: deleting.id, archived: true });
            setDeleting(null);
          }}
        />
      ) : null}
    </AreaGate>
  );
}

/**
 * §2.4 — three outcomes the UI must not blur.
 *
 * A system nothing has pinned is deleted outright. A system some leaderboard has been scored by
 * cannot be, and the server refuses it with a 409 whose message leads with Archive rather than
 * with the failure. So the dialog offers Archive **before** the attempt as well as after one: the
 * common case for any system that has actually been used is that archiving is what was wanted.
 */
function DeleteDialog({
  system,
  onClose,
  onArchived,
}: {
  system: PointsSystem;
  onClose: () => void;
  onArchived: () => void;
}) {
  const remove = useDeletePointsSystem();

  return (
    <Dialog
      title={`Delete ${system.name}?`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-ink-800/15 py-2 text-sm text-ink-800 hover:bg-ink-800/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onArchived}
            className="flex-1 rounded-lg bg-felt-700 py-2 text-sm font-medium text-white hover:bg-felt-900"
          >
            Archive instead
          </button>
          <button
            type="button"
            onClick={() => remove.mutate(system.id, { onSuccess: onClose })}
            disabled={remove.isPending}
            className="flex-1 rounded-lg border border-chip-500/40 py-2 text-sm font-medium text-chip-500 hover:bg-chip-500/5 disabled:opacity-40"
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm leading-relaxed text-ink-800/80">
        <p>
          <strong className="text-ink-950">Archiving</strong> hides it from every room’s picker and
          leaves every leaderboard that used it intact and still scoring. It can be restored.
        </p>
        <p>
          <strong className="text-ink-950">Deleting</strong> removes the system and all its
          versions for good, and only works if no leaderboard has ever pinned one of them. If one
          has, the server will refuse — and archiving is what you wanted.
        </p>
        <ErrorNotice message={describeApiError(remove.error)} />
      </div>
    </Dialog>
  );
}
